import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, School, Mail, Phone, Calendar, 
  Filter, RefreshCw, Sparkles, GraduationCap,
  ChevronLeft, MessageSquare, Clock, LogOut
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  new: { label: 'Nový', color: 'bg-blue-100 text-blue-700' },
  contacted: { label: 'Kontaktováno', color: 'bg-yellow-100 text-yellow-700' },
  interested: { label: 'Má zájem', color: 'bg-emerald-100 text-emerald-700' },
  booked: { label: 'Rezervováno', color: 'bg-purple-100 text-purple-700' },
  completed: { label: 'Dokončeno', color: 'bg-slate-100 text-slate-700' },
  cancelled: { label: 'Zrušeno', color: 'bg-red-100 text-red-700' }
};

const courseLabels = {
  ms: 'MŠ',
  zs: 'ZŠ',
  oba: 'MŠ + ZŠ'
};

const positionLabels = {
  ucitel: 'Učitel/ka',
  reditel: 'Ředitel/ka',
  zastupce: 'Zástupce',
  metodik: 'Metodik',
  koordinator: 'ICT koordinátor',
  jine: 'Jiná'
};

const formatLabels = {
  prezencne: 'Prezenčně',
  online: 'Online',
  nezalezi: 'Nezáleží'
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  // Check authentication
  useEffect(() => {
    const isAuth = sessionStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/tym-login');
    }
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    sessionStorage.removeItem('adminUser');
    toast.success('Odhlášení úspěšné');
    navigate('/');
  };
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCourse, setFilterCourse] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchLeads = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterCourse !== 'all') params.course = filterCourse;
      
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, { params }),
        axios.get(`${API}/leads/stats/summary`)
      ]);
      
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Nepodařilo se načíst data');
    } finally {
      setIsLoading(false);
    }
  }, [filterStatus, filterCourse]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status: newStatus });
      toast.success('Status aktualizován');
      fetchLeads();
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Nepodařilo se aktualizovat status');
    }
  };

  const updateLeadNotes = async (leadId, notes) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { notes });
      toast.success('Poznámky uloženy');
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, notes }));
      }
    } catch (error) {
      console.error('Error updating notes:', error);
      toast.error('Nepodařilo se uložit poznámky');
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(search) ||
      lead.email?.toLowerCase().includes(search) ||
      lead.school?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('cs-CZ', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-slate-500 hover:text-slate-700">
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <h1 className="text-xl font-bold text-slate-900">Správa poptávek</h1>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={fetchLeads} variant="outline" size="sm">
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Obnovit
              </Button>
              <Button onClick={handleLogout} variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
                <LogOut className="w-4 h-4 mr-2" />
                Odhlásit
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                  <p className="text-sm text-slate-500">Celkem</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.by_status?.new || 0}</p>
                  <p className="text-sm text-slate-500">Nových</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.by_course?.ms || 0}</p>
                  <p className="text-sm text-slate-500">MŠ</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.by_course?.zs || 0}</p>
                  <p className="text-sm text-slate-500">ZŠ</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Hledat podle jména, emailu nebo školy..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-10"
              />
            </div>
            <div className="flex gap-3">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[160px] h-10">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Všechny statusy</SelectItem>
                  <SelectItem value="new">Nový</SelectItem>
                  <SelectItem value="contacted">Kontaktováno</SelectItem>
                  <SelectItem value="interested">Má zájem</SelectItem>
                  <SelectItem value="booked">Rezervováno</SelectItem>
                  <SelectItem value="completed">Dokončeno</SelectItem>
                  <SelectItem value="cancelled">Zrušeno</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterCourse} onValueChange={setFilterCourse}>
                <SelectTrigger className="w-[140px] h-10">
                  <SelectValue placeholder="Kurz" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Všechny kurzy</SelectItem>
                  <SelectItem value="ms">MŠ</SelectItem>
                  <SelectItem value="zs">ZŠ</SelectItem>
                  <SelectItem value="oba">Oba</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400" />
              <p className="mt-4 text-slate-500">Načítám data...</p>
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto text-slate-300" />
              <p className="mt-4 text-slate-500">Žádné poptávky</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Kontakt</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Škola</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Kurz</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Forma</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Datum</th>
                    <th className="px-6 py-4"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLeads.map((lead) => (
                    <tr 
                      key={lead.id} 
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedLead(lead)}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900">{lead.name}</p>
                          <p className="text-sm text-slate-500">{lead.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <School className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700">{lead.school}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {lead.course_interest && (
                          <Badge variant="outline" className={lead.course_interest === 'ms' ? 'border-emerald-200 text-emerald-700' : 'border-blue-200 text-blue-700'}>
                            {courseLabels[lead.course_interest] || lead.course_interest}
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-600">
                          {formatLabels[lead.preferred_format] || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig[lead.status]?.color || 'bg-slate-100 text-slate-700'}`}>
                          {statusConfig[lead.status]?.label || lead.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-500">{formatDate(lead.created_at)}</span>
                      </td>
                      <td className="px-6 py-4">
                        {lead.notes && <MessageSquare className="w-4 h-4 text-slate-400" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Lead Detail Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl">{selectedLead?.name}</DialogTitle>
            <DialogDescription>Detail poptávky</DialogDescription>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-6 mt-4">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">E-mail</p>
                  <a href={`mailto:${selectedLead.email}`} className="flex items-center gap-2 text-teal-600 hover:underline">
                    <Mail className="w-4 h-4" />
                    {selectedLead.email}
                  </a>
                </div>
                {selectedLead.phone && (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Telefon</p>
                    <a href={`tel:${selectedLead.phone}`} className="flex items-center gap-2 text-teal-600 hover:underline">
                      <Phone className="w-4 h-4" />
                      {selectedLead.phone}
                    </a>
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Škola</p>
                  <p className="font-medium">{selectedLead.school}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pozice</p>
                  <p className="font-medium">{positionLabels[selectedLead.position] || selectedLead.position || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Kurz</p>
                  <p className="font-medium">{courseLabels[selectedLead.course_interest] || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Forma</p>
                  <p className="font-medium">{formatLabels[selectedLead.preferred_format] || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Počet pedagogů</p>
                  <p className="font-medium">{selectedLead.participants_count || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">OP JAK II</p>
                  <p className="font-medium">{selectedLead.op_jak_interest ? 'Ano' : 'Ne'}</p>
                </div>
              </div>

              {/* Message */}
              {selectedLead.message && (
                <div className="space-y-2">
                  <p className="text-sm text-slate-500">Zpráva</p>
                  <p className="p-4 bg-slate-50 rounded-lg text-slate-700">{selectedLead.message}</p>
                </div>
              )}

              {/* Status */}
              <div className="space-y-2">
                <p className="text-sm text-slate-500">Status</p>
                <Select 
                  value={selectedLead.status} 
                  onValueChange={(value) => updateLeadStatus(selectedLead.id, value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Nový</SelectItem>
                    <SelectItem value="contacted">Kontaktováno</SelectItem>
                    <SelectItem value="interested">Má zájem</SelectItem>
                    <SelectItem value="booked">Rezervováno</SelectItem>
                    <SelectItem value="completed">Dokončeno</SelectItem>
                    <SelectItem value="cancelled">Zrušeno</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <p className="text-sm text-slate-500">Poznámky</p>
                <Textarea
                  defaultValue={selectedLead.notes || ''}
                  placeholder="Přidejte poznámky k této poptávce..."
                  rows={3}
                  onBlur={(e) => {
                    if (e.target.value !== selectedLead.notes) {
                      updateLeadNotes(selectedLead.id, e.target.value);
                    }
                  }}
                />
              </div>

              {/* Timestamps */}
              <div className="flex items-center gap-4 text-sm text-slate-500 pt-4 border-t">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Vytvořeno: {formatDate(selectedLead.created_at)}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
